<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import {
  ArticleAllocationListDataQuery,
  ArticleAllocationListDataQueryVariables,
  ArticleAllocationListPlacementDataQuery,
  ArticleAllocationListPlacementDataQueryVariables,
  ArticleAllocationListSetEbomAllocationMutation,
  ArticleAllocationListSetEbomAllocationMutationVariables,
  PartAllocationListAllocationMutationMutation,
  PartAllocationListAllocationMutationMutationVariables,
  PermissionOrganization,
} from '@/generated/graphql'
import { computed, ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import ArticleQuantity from '@/components/article/ArticleQuantity.vue'
import AllocationQuantityEditor from '@/components/article/AllocationQuantityEditor.vue'
import ArticleName from '@/components/article/ArticleName.vue'
import EbomPartAllocationRow from '@/components/article/EbomPartAllocationRow.vue'
import ManualArticleAllocationRow from '@/components/article/ManualArticleAllocationRow.vue'
import IconManual from '@/components/icons/IconManual.vue'
import IconEbom from '@/components/icons/IconEbom.vue'
import ButtonDelete from '@/components/button/ButtonDelete.vue'
import IconArticleType from '@/components/icons/IconArticleType.vue'
import AsyncDeleteDialog from '@/components/dialogs/AsyncDeleteDialog.vue'
import DeleteDialogNext from '@/components/dialogs/AsyncDeleteDialog.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import useAuthorization from '@/composables/useAuthorization'
import { useRouter } from 'vue-router'
import { useStepArticleColors } from '@/composables/useStepArticleColors'

const props = defineProps<{
  label: string
  bopId?: string
  stepId: string
  // When set, allocations are local to this library-reference placement instead of the step.
  graphNodeStepId?: string
  showColors?: boolean
  readonly?: boolean
}>()

const emit = defineEmits<{ updated: [] }>()

const { swatchColor, refresh: refreshColors } = useStepArticleColors(
  computed(() => props.stepId),
  computed(() => props.graphNodeStepId),
)

type LocalArticleAllocation =
  ArticleAllocationListDataQuery['product']['step']['articleAllocations'][0]

const { t } = useI18n()
const { hasOrgAuthz } = useAuthorization()
const canEdit = computed(
  () => !props.readonly && hasOrgAuthz(PermissionOrganization.OrgProductWrite),
)
const router = useRouter()

const stepFetchQuery = useQuery<
  ArticleAllocationListDataQuery,
  ArticleAllocationListDataQueryVariables
>(
  gql`
    query ArticleAllocationListData($stepId: ID!) {
      product {
        step(id: $stepId) {
          articleAllocations {
            __typename
            ... on IArticleAllocation {
              id
              article {
                id
                articleNumber
                revision
                name
                unit
                type
              }
              milliQuantity
            }
            ... on EbomPartAllocation {
              ebomPart {
                id
                ebomId
                articleNumber
                revision
                name
                unit
                milliQuantity
                articlePath {
                  articleNumbers
                }
                stepAllocations {
                  id
                  milliQuantity
                }
              }
            }
          }
        }
      }
    }
  `,
  () => ({ stepId: props.stepId }),
  () => ({ enabled: !props.graphNodeStepId }),
)

const placementFetchQuery = useQuery<
  ArticleAllocationListPlacementDataQuery,
  ArticleAllocationListPlacementDataQueryVariables
>(
  gql`
    query ArticleAllocationListPlacementData($graphNodeStepId: ID!) {
      product {
        placementArticleAllocations(graphNodeStepId: $graphNodeStepId) {
          __typename
          ... on IArticleAllocation {
            id
            article {
              id
              articleNumber
              revision
              name
              unit
              type
            }
            milliQuantity
          }
          ... on EbomPartAllocation {
            ebomPart {
              id
              ebomId
              articleNumber
              revision
              name
              unit
              milliQuantity
              articlePath {
                articleNumbers
              }
              stepAllocations {
                id
                milliQuantity
              }
            }
          }
        }
      }
    }
  `,
  () => ({ graphNodeStepId: props.graphNodeStepId ?? '' }),
  () => ({ enabled: !!props.graphNodeStepId }),
)

const loading = computed(() => stepFetchQuery.loading.value || placementFetchQuery.loading.value)
const allocations = computed<LocalArticleAllocation[]>(() => {
  const items = props.graphNodeStepId
    ? ((placementFetchQuery.result.value?.product.placementArticleAllocations ??
        []) as LocalArticleAllocation[])
    : (stepFetchQuery.result.value?.product.step.articleAllocations ?? [])
  // The API returns allocations in unspecified order; anchor ties so rows do not shuffle on refetch.
  return [...items].sort((a, b) => a.id.localeCompare(b.id))
})
const refetch = () => {
  refreshColors()
  return props.graphNodeStepId ? placementFetchQuery.refetch() : stepFetchQuery.refetch()
}

const deleteDialog = ref<ComponentExposed<typeof DeleteDialogNext>>()
const setManualAllocationMutation = useMutation<
  PartAllocationListAllocationMutationMutation,
  PartAllocationListAllocationMutationMutationVariables
>(gql`
  mutation PartAllocationListAllocationMutation($command: AllocateArticleToStepCommand!) {
    article {
      allocateArticleToStep(command: $command)
    }
  }
`)

const deleteManualAllocation = async (articleId: string) => {
  await deleteDialog.value?.open(t('entity.manualPartAllocation.singular'), () =>
    setManualAllocationMutation
      .mutate({
        command: {
          articleId,
          stepId: props.stepId,
          milliQuantity: 0,
          graphNodeStepId: props.graphNodeStepId,
        },
      })
      .then(() => {
        refetch()
        emit('updated')
      }),
  )
}

const setEbomAllocationMutation = useMutation<
  ArticleAllocationListSetEbomAllocationMutation,
  ArticleAllocationListSetEbomAllocationMutationVariables
>(
  gql`
    mutation ArticleAllocationListSetEbomAllocation($command: AllocateEbomPartToStepCommand!) {
      article {
        allocateEbomPartToStep(command: $command)
      }
    }
  `,
  () => ({ refetchQueries: ['EbomPartPickerData'] }),
)
const deleteEbomPartAllocation = async (ebomPartId: string) => {
  await deleteDialog.value?.open(t('entity.ebomPartAllocation.singular'), () =>
    setEbomAllocationMutation
      .mutate({
        command: {
          ebomPartId,
          stepId: props.stepId,
          milliQuantity: 0,
          graphNodeStepId: props.graphNodeStepId,
        },
      })
      .then(() => {
        refetch()
        emit('updated')
      }),
  )
}

const quantityMutationLoading = computed(
  () => setManualAllocationMutation.loading.value || setEbomAllocationMutation.loading.value,
)
const updateAllocationQuantity = async (item: LocalArticleAllocation, milliQuantity: number) => {
  if (item.__typename == 'EbomPartAllocation') {
    await setEbomAllocationMutation.mutate({
      command: {
        ebomPartId: item.ebomPart.id,
        stepId: props.stepId,
        milliQuantity,
        graphNodeStepId: props.graphNodeStepId,
      },
    })
  } else {
    await setManualAllocationMutation.mutate({
      command: {
        articleId: item.article.id,
        stepId: props.stepId,
        milliQuantity,
        graphNodeStepId: props.graphNodeStepId,
      },
    })
  }
  refetch()
  emit('updated')
}

const allocatedMilliQuantity = (item: LocalArticleAllocation) =>
  item.__typename == 'EbomPartAllocation'
    ? item.ebomPart.stepAllocations.reduce((agg, a) => agg + a.milliQuantity, 0)
    : 0
const netMilliQuantity = (item: LocalArticleAllocation) =>
  item.__typename == 'EbomPartAllocation'
    ? allocatedMilliQuantity(item) - item.ebomPart.milliQuantity
    : 0

const headers = [
  {
    key: '__typename',
    title: t('entity.article.field.source'),
    width: 90,
  },
  {
    key: 'article.type',
    title: t('entity.article.field.type'),
    width: 90,
  },
  {
    key: 'article.name',
    title: t('entity.article.singular'),
  },
  {
    key: 'milliQuantity',
    title: t('entity.article.field.quantity'),
    width: 180,
  },
  {
    key: 'actions',
    width: 100,
    sortable: false,
    align: 'end' as const,
  },
]
const sort = [
  { key: '__typename', order: 'asc' as const },
  { key: 'article.type', order: 'asc' as const },
  { key: 'article.name', order: 'asc' as const },
]
</script>

<template>
  <v-card class="allocations">
    <v-data-table-virtual
      :items="allocations"
      :headers="headers"
      :loading="loading"
      :sort-by="sort"
      :loading-text="t('component.dataTable.loading')"
      :no-data-text="t('component.dataTable.noData')"
      density="comfortable"
      fixed-header
    >
      <template v-if="canEdit" #body.prepend>
        <manual-article-allocation-row
          :step-id="props.stepId"
          :graph-node-step-id="props.graphNodeStepId"
          @allocated="
            () => {
              refetch()
              emit('updated')
            }
          "
        />
        <ebom-part-allocation-row
          v-if="hasOrgAuthz(PermissionOrganization.OrgEbomRead) && props.bopId"
          :bop-id="props.bopId"
          :step-id="props.stepId"
          :graph-node-step-id="props.graphNodeStepId"
          @allocated="
            () => {
              refetch()
              emit('updated')
            }
          "
        />
      </template>
      <template #item.actions="{ item }: { item: LocalArticleAllocation }">
        <icon-ebom
          v-if="item.__typename == 'EbomPartAllocation'"
          @click="
            hasOrgAuthz(PermissionOrganization.OrgEbomRead)
              ? router.push({
                  name: 'ebom',
                  params: { ebomId: item.ebomPart.ebomId, ebomPartId: item.ebomPart.id },
                })
              : undefined
          "
        />
        <button-delete
          v-if="canEdit && item.__typename == 'ManualArticleAllocation'"
          :loading="setManualAllocationMutation.loading.value"
          @click="deleteManualAllocation(item.article.id)"
        />
        <button-delete
          v-if="canEdit && item.__typename == 'EbomPartAllocation'"
          :loading="setEbomAllocationMutation.loading.value"
          @click="deleteEbomPartAllocation(item.ebomPart.id)"
        />
      </template>
      <template #item.__typename="{ item }: { item: LocalArticleAllocation }">
        <icon-manual v-if="item.__typename == 'ManualArticleAllocation'" />
        <icon-ebom v-if="item.__typename == 'EbomPartAllocation'" />
      </template>
      <template #item.article.type="{ item }: { item: LocalArticleAllocation }">
        <icon-article-type :type="item.article.type" />
      </template>
      <template #item.article.name="{ item }: { item: LocalArticleAllocation }">
        <div class="d-flex" style="gap: 6px">
          <span
            v-if="showColors"
            class="color-dot color-dot--md"
            :style="{
              marginTop: '3px',
              flexShrink: 0,
              backgroundColor: swatchColor(item.article.articleNumber),
            }"
          />
          <div>
            <article-name
              :article="item.__typename == 'EbomPartAllocation' ? item.ebomPart : item.article"
            />
          </div>
        </div>
      </template>
      <template #item.milliQuantity="{ item }: { item: LocalArticleAllocation }">
        <div class="d-flex align-center" style="gap: 4px">
          <allocation-quantity-editor
            v-if="canEdit"
            :milli-quantity="item.milliQuantity"
            :unit="item.article.unit"
            :loading="quantityMutationLoading"
            @change="(milliQuantity) => updateAllocationQuantity(item, milliQuantity)"
          />
          <article-quantity
            v-else
            :milli-quantity="item.milliQuantity"
            :unit="item.article.unit"
            unit-abbreviated
          />
          <v-tooltip
            v-if="item.__typename == 'EbomPartAllocation' && netMilliQuantity(item) != 0"
            location="top"
          >
            <template #activator="{ props: activatorProps }">
              <v-chip v-bind="activatorProps" size="small" color="error">
                <article-quantity :milli-quantity="netMilliQuantity(item)" />
              </v-chip>
            </template>
            <span>
              {{ t('component.articleAllocationList.inEbom') }}:
              <article-quantity
                :milli-quantity="item.ebomPart.milliQuantity"
                :unit="item.article.unit"
                unit-abbreviated
              />
              · {{ t('component.articleAllocationList.allocatedToSteps') }}:
              <article-quantity
                :milli-quantity="allocatedMilliQuantity(item)"
                :unit="item.article.unit"
                unit-abbreviated
              />
            </span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table-virtual>
  </v-card>

  <async-delete-dialog ref="deleteDialog" />
</template>

<style scoped lang="scss">
// A step can hold hundreds of EBOM parts; capping the height lets the table render only the
// rows in view. Those rows alone would size content-based columns, so the layout is fixed.
.allocations :deep(.v-table__wrapper) {
  max-height: 70vh;
}
.allocations :deep(table) {
  table-layout: fixed;
}
</style>
